import api from 'api/api'
import { getClientShare, recoverLocalWalletTextWithShares } from 'lib/bsv-signing'
import { isRunningInExpoWebView, shareFileNative } from 'utils/nativeShare'
import { withSeedWordsFromServer } from 'lib/walletPasskeyRecovery'

// Where the wallet key file comes from (User#wallet_backup_source): the whole
// wallet Treechat holds, or rebuilt from this browser's client-signing share
// plus the server's.
export type WalletBackupSource = 'server' | 'client_shares'
export type WalletBackupDelivery = 'download' | 'native_share'

export class ClientWalletShareUnavailableError extends Error {}

export const WALLET_BACKUP_FILENAME = 'treechat_wallet_v1.json'

async function walletBackupBlob(source: WalletBackupSource, userId: string): Promise<Blob> {
    if (source === 'server') {
        return api.downloadBsvWallet()
    }

    const localShareRecord = await getClientShare(userId)
    if (!localShareRecord?.clientShare) {
        throw new ClientWalletShareUnavailableError(
            'This browser does not have the wallet share needed to recover your keys.',
        )
    }

    const serverShare = await api.getBsvServerShare(userId, localShareRecord.deviceId)
    if (!serverShare?.treechatShare) {
        throw new ClientWalletShareUnavailableError(
            'This browser does not have a matching server wallet share.',
        )
    }

    const walletText = await withSeedWordsFromServer(
        await recoverLocalWalletTextWithShares(
            userId,
            serverShare.treechatShare,
            localShareRecord.clientShare,
        ),
    )
    return new Blob([walletText], { type: 'application/json' })
}

export async function walletBackupText(
    source: WalletBackupSource,
    userId: string,
): Promise<string> {
    return (await walletBackupBlob(source, userId)).text()
}

// Saves the wallet key file: through the share sheet inside the mobile app
// (its WebView has no download handling), as a download everywhere else.
export async function downloadWalletBackup(
    source: WalletBackupSource,
    userId: string,
    {
        beforeBrowserDownload,
    }: {
        // iOS Safari cancels requests in flight when a download starts ("Load failed"),
        // so a request that must land goes here and finishes before the download.
        beforeBrowserDownload?: () => Promise<void>
    } = {},
): Promise<WalletBackupDelivery> {
    const walletBlob = await walletBackupBlob(source, userId)

    const shared = await shareFileNative({
        blob: walletBlob,
        fileName: WALLET_BACKUP_FILENAME,
        mimeType: 'application/json',
    })
    // Native file sharing reports that the sheet closed, not that the user
    // actually saved the file. The deletion flow asks for explicit confirmation.
    if (shared) return 'native_share'

    if (isRunningInExpoWebView()) {
        throw new Error(
            'Update the Treechat app to save your wallet keys before deleting your account.',
        )
    }

    await beforeBrowserDownload?.()
    const url = window.URL.createObjectURL(walletBlob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', WALLET_BACKUP_FILENAME)
    document.body.appendChild(link)
    link.click()
    link.parentNode?.removeChild(link)
    window.URL.revokeObjectURL(url)
    return 'download'
}
